import { useLang } from 'hooks/lang';

import { actions } from 'entities';

import {
  Column,
  LabeledInput,
  Row,
  Label,
  Button,
  LabeledTextarea,
  Slider,
} from 'components/Blocks';

import { useRequestRecruitmentModal } from './requestRecruitment.context';

import { pick } from 'utils/object';
import { SAMPLE_GENDER_OPTIONS } from 'utils/constants';
import {
  ChatIcon,
  DollarBillIcon,
  FileSearchIcon,
  LocationMapIcon,
  UserSpeakIcon,
} from 'components/Icons';
import { cn } from 'styles';
import {
  isValidJustification,
  RequiredJustificationAlert,
} from 'components/RequiredJustificationAlert';

export function TargetForm() {
  const { t } = useLang();

  const { form, close, task, updateTask } = useRequestRecruitmentModal();

  const [ageMin, ageMax] = form.state.ageRange;

  const canRequest = [
    Object.values(form.state).every(Boolean),
    ageMin <= ageMax,
    isValidJustification(form.state.requestComment),
  ].every(Boolean);

  const requestRecruitment = actions.projects.requestRecruitment.use({
    block: !canRequest,

    params: {
      projectId: task.projectId,
      taskId: task.id,

      ...pick(form.state, [
        'country',
        'gender',
        'region',
        'requestComment',
        'sampleId',
        'selIncome',
      ]),

      completesNecessary: Number(form.state.completesNecessary),

      ageRange: {
        from: ageMin,
        to: ageMax,
      },
    },

    onSuccess: () => {
      updateTask((old) => ({
        ...old,

        activeRecruitments: (old.activeRecruitments ?? 0) + 1,
      }));

      close();
    },
  });

  return (
    <Column className="mt-2">
      <Column gap="2">
        <Label>{t('gender')}</Label>

        <Row className="phone:flex-col phone:items-stretch">
          {SAMPLE_GENDER_OPTIONS.map(({ Icon, getLabel, sampleId }) => (
            <button
              key={sampleId}
              onClick={() => form.dispatch({ gender: sampleId })}
              className={cn(
                'flex w-full items-center justify-center gap-2 rounded-lg border px-2 py-1 transition-colors phone:justify-start',
                form.state.gender == sampleId
                  ? 'border-primary text-primary'
                  : 'border-transparent hover:border-primary hover:text-primary',
              )}
            >
              <Icon />
              {getLabel(t)}
            </button>
          ))}
        </Row>
      </Column>

      <Column gap="2" fullWidth>
        <Label>{t('age')}</Label>

        <Row className="p-2">
          <div className="w-10 select-none text-center text-sm font-medium">{ageMin}</div>
          <Slider
            min={13}
            max={99}
            size="medium"
            value={form.state.ageRange}
            name="ageRange"
            onChange={form.handleChange}
            type="range"
            color="secondary"
            rangeClassName="bg-foreground"
            thumbClassName="bg-border border-border"
          />
          <div className="w-10 select-none text-center text-sm font-medium">{ageMax}</div>
        </Row>
      </Column>

      <div className="grid grid-cols-2 gap-4 phone:grid-cols-1">
        <LabeledInput
          name="region"
          label={t('region')}
          labelIcon={LocationMapIcon}
          value={form.state.region}
          onChange={form.handleChange}
          fullWidth
        />

        <LabeledInput
          name="selIncome"
          label={t('selIncome')}
          labelIcon={DollarBillIcon}
          value={form.state.selIncome}
          onChange={form.handleChange}
          fullWidth
        />

        <LabeledInput
          onChange={form.handleChange}
          label={t('completes-necessary')!}
          labelIcon={FileSearchIcon}
          value={form.state.completesNecessary}
          name="completesNecessary"
          type="number"
          fullWidth
          containerClassName="col-span-2 phone:col-span-1"
        />
      </div>

      <LabeledTextarea
        name="requestComment"
        label={t('comments')!}
        labelIcon={ChatIcon}
        value={form.state.requestComment}
        onChange={form.handleChange}
      />

      <RequiredJustificationAlert text={form.state.requestComment} />

      <Button disabled={!canRequest} {...requestRecruitment.buttonProps} className="ml-auto">
        <UserSpeakIcon />
        {t('request')}
      </Button>
    </Column>
  );
}
